import { useEffect, useRef, useState, type ReactNode } from 'react';
import { createPortal } from 'react-dom';
import { useT } from '../i18n';

/**
 * Bottom sheet rendered into <body> so no ancestor stacking context (e.g. a
 * full-screen map page) can push it under the bottom navigation. The content
 * scrolls inside the sheet; dragging the handle down dismisses it.
 */
export function Sheet({ open, onClose, children, label }: { open: boolean; onClose: () => void; children: ReactNode; label: string }) {
  const [dragY, setDragY] = useState(0);
  const t = useT();
  const startY = useRef<number | null>(null);

  useEffect(() => {
    if (!open) return;
    setDragY(0);
    const onKey = (e: KeyboardEvent) => e.key === 'Escape' && onClose();
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [open, onClose]);

  if (!open) return null;

  const onTouchStart = (e: React.TouchEvent) => {
    startY.current = e.touches[0]!.clientY;
  };
  const onTouchMove = (e: React.TouchEvent) => {
    if (startY.current === null) return;
    setDragY(Math.max(0, e.touches[0]!.clientY - startY.current));
  };
  const onTouchEnd = () => {
    if (dragY > 90) onClose();
    else setDragY(0);
    startY.current = null;
  };

  return createPortal(
    <div className="fixed inset-0 z-[60] flex items-end justify-center" role="dialog" aria-modal="true" aria-label={label}>
      <button className="absolute inset-0 bg-black/50" aria-label={t('Close')} onClick={onClose} />
      <div
        className="animate-pop relative flex max-h-[85dvh] w-full max-w-lg flex-col rounded-t-3xl border-t border-ink-700 bg-ink-850 shadow-2xl"
        style={{ transform: dragY ? `translateY(${dragY}px)` : undefined, transition: dragY ? 'none' : 'transform .2s' }}
      >
        <div
          className="flex shrink-0 cursor-grab touch-none justify-center pb-2 pt-3"
          onTouchStart={onTouchStart}
          onTouchMove={onTouchMove}
          onTouchEnd={onTouchEnd}
        >
          <div className="h-1.5 w-10 rounded-full bg-ink-600" />
        </div>
        <div className="overflow-y-auto overscroll-contain px-5 pb-[calc(var(--safe-bottom)+20px)]">{children}</div>
      </div>
    </div>,
    document.body,
  );
}
